import { useCallback, useEffect, useRef } from 'react';
import { pushOverlayEntry, type OverlayHistoryHandle } from './overlayHistory';

/**
 * While `open`, Back closes the overlay by calling `onDismiss`. Returns
 * `release()`, which overlays call when they close themselves; it resolves
 * once the history entry is gone and no other overlay traversal is pending.
 */
export function useOverlayHistory(open: boolean, onDismiss: () => void, enabled = true): () => Promise<void> {
  const handleRef = useRef<OverlayHistoryHandle | null>(null);
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  useEffect(() => {
    if (!open || !enabled) return;
    let cancelled = false;
    let handle: OverlayHistoryHandle | null = null;
    // Deferred so StrictMode's synchronous mount/unmount/mount pushes one entry, not three.
    queueMicrotask(() => {
      if (cancelled) return;
      handle = pushOverlayEntry(() => {
        handleRef.current = null;
        dismissRef.current();
      });
      handleRef.current = handle;
    });
    return () => {
      cancelled = true;
      if (handle && handleRef.current === handle) {
        handleRef.current = null;
        void handle.release();
      }
    };
  }, [open, enabled]);

  return useCallback(() => {
    const handle = handleRef.current;
    handleRef.current = null;
    return handle ? handle.release() : Promise.resolve();
  }, []);
}
